import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import DatePicker from 'shared_items/DatePicker';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import { deleteManyFlowPreliminaryTransactions } from 'views/finance/moduleServices/flow_requests';

const emptyRange = () => ({ start: '', end: '' });

/**
 * Delete all or a date range of preliminary flow rows (server: delete_many).
 */
const ModalDeletePreliminaryBatch = ({ isOpen, onClose, onDeleted }) => {
  const [scope, setScope] = useState('all');
  const [rangeValue, setRangeValue] = useState(emptyRange);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setScope('all');
    setRangeValue(emptyRange());
    setSubmitting(false);
  }, [isOpen]);

  const handleRangeChange = (e) => {
    const v = e?.target?.value;
    if (v && typeof v === 'object') {
      setRangeValue({
        start: typeof v.start === 'string' ? v.start : '',
        end: typeof v.end === 'string' ? v.end : '',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload;
    if (scope === 'all') {
      payload = { scope: 'all' };
    } else {
      const start = (rangeValue.start || '').trim();
      const end = (rangeValue.end || '').trim();
      if (!start || !end) {
        toast.error('Select start and end dates for the range.');
        return;
      }
      if (start > end) {
        toast.error('Start date must be on or before end date.');
        return;
      }
      payload = { scope: 'range', from_date: start, to_date: end };
    }

    try {
      setSubmitting(true);
      const res = await deleteManyFlowPreliminaryTransactions(payload);
      const n = res?.deleted ?? 0;
      toast.success(res?.detail || `${n} row(s) deleted.`);
      await Promise.resolve(onDeleted?.());
      onClose?.();
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d === 'string' ? d : d?.error || d?.detail) ||
        err.message ||
        'Delete failed';
      toast.error(typeof msg === 'string' ? msg : 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex justify-center items-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md relative max-h-[90vh] flex flex-col">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 pr-2">Delete preliminary data</h2>
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
            className="text-gray-500 hover:text-gray-700 shrink-0 p-1 rounded-lg hover:bg-gray-100"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-4 py-3 space-y-4 text-sm text-gray-700">
            <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              This cannot be undone. Only your preliminary (staging) rows are affected — official Flow
              transactions are not changed.
            </p>

            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold text-gray-600 mb-2">What to delete</legend>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="prelim-delete-scope"
                  className="accent-green-700"
                  checked={scope === 'all'}
                  onChange={() => setScope('all')}
                  disabled={submitting}
                />
                <span>All preliminary data</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="prelim-delete-scope"
                  className="accent-green-700"
                  checked={scope === 'range'}
                  onChange={() => setScope('range')}
                  disabled={submitting}
                />
                <span>Specific date range (by transaction date)</span>
              </label>
            </fieldset>

            {scope === 'range' && (
              <div className="space-y-1.5">
                <span className="block text-xs font-medium text-gray-600">Date range</span>
                <DatePicker
                  name="prelim_delete_range"
                  value={rangeValue}
                  onChange={handleRangeChange}
                  mode="range"
                  colorScheme="green"
                  placeholder="Select range"
                  className="w-full justify-between min-h-[2.25rem] border border-gray-300 bg-white shadow-sm"
                  dropdownZIndex={61}
                />
                <p className="text-[11px] text-gray-500">
                  Rows with <strong>date</strong> between the selected bounds (inclusive) will be removed.
                </p>
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap justify-end gap-2 shrink-0 bg-gray-50 rounded-b-2xl">
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              disabled={submitting}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
            >
              {submitting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalDeletePreliminaryBatch;
